import { useCallback, useEffect, useState } from 'react';

import {
    AlertTriangle,
    ArrowRight,
    Building2,
    CheckCircle2,
    ChevronRight,
    Clock,
    Database,
    Loader2,
    MapPin,
    RefreshCw,
    Search,
    Trash2,
    Wand2,
    X,
    Zap,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Progress } from '@/components/ui/shadcn/progress';
import { ScrollArea } from '@/components/ui/shadcn/scroll-area';
import { Separator } from '@/components/ui/shadcn/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CleanupStats {
    total_companies: number;
    active_companies: number;
    soft_deleted_companies: number;
    already_merged_companies: number;
    duplicate_groups: number;
    registry_mirror_entries?: number;
    registry_candidates?: number;
    registry_pending_review?: number;
    registry_matched?: number;
}

interface RegistryCandidate {
    uuid: string;
    registry_name?: string | null;
    registry_code?: string | null;
    registry_vat?: string | null;
    registry_status?: string | null;
    confidence_score: number;
    name_score: number;
    code_match: boolean;
    matched_by?: string | null;
    name_disagrees: boolean;
    address?: string | null;
}

interface RegistryReviewCompany {
    uuid: string;
    status: string;
    current: { name: string; company_code?: string | null; vat?: string | null; address?: string | null };
    candidates: RegistryCandidate[];
}

interface CompanyInfo {
    uuid: string;
    name: string;
    company_code?: string | null;
    country?: string | null;
}

interface GroupItem {
    company_uuid: string;
    role: string;
    company: CompanyInfo | null;
    linked_records_count: number;
    linked_records?: Record<string, number>;
    linked_records_total?: number;
}

interface MergeGroup {
    uuid: string;
    status: 'pending' | 'approved' | 'merged' | 'rejected';
    confidence_score: number;
    suggested_surviving_company_uuid: string | null;
    approved_surviving_company_uuid: string | null;
    reason?: Record<string, unknown>;
    risk?: string | null;
    enrichedItems?: GroupItem[];
    items?: GroupItem[];
}

interface AuditEntry {
    uuid: string;
    performed_at: string;
    source_company_uuid: string;
    target_company_uuid: string;
    source_company_name?: string;
    target_company_name?: string;
    moved_records_summary?: Record<string, number>;
    reversible?: boolean;
    reversed_at?: string | null;
}

// Human-readable labels for the backend's risk reason codes.
const RISK_LABELS: Record<string, string> = {
    different_valid_codes: 'Different registration codes',
    different_vat: 'Different VAT numbers',
    different_country: 'Different countries',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const BASE = '/settings/data-import/company-cleanup';

const statusColor: Record<string, string> = {
    pending: 'bg-warning-subtle text-warning-subtle-foreground',
    approved: 'bg-info-subtle text-info-subtle-foreground',
    merged: 'bg-success-subtle text-success-subtle-foreground',
    rejected: 'bg-danger-subtle text-danger-subtle-foreground',
};

function ConfidenceBadge({ score }: { score: number }) {
    const pct = Math.round(score);
    const color =
        pct >= 80
            ? 'text-success-subtle-foreground bg-success-subtle'
            : pct >= 50
              ? 'text-warning-subtle-foreground bg-warning-subtle'
              : 'text-danger-subtle-foreground bg-danger-subtle';
    return <span className={cn('inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums', color)}>{pct}%</span>;
}

function CodeBadge({ code }: { code?: string | null }) {
    if (!code) return <span className="text-muted-foreground text-xs italic">no code</span>;
    // Only flag codes that are explicitly placeholder/legacy — not all non-9-digit codes,
    // since foreign company codes (PL, DE, etc.) have different valid formats.
    const isLegacy = /^LEGACY/i.test(code) || /^PLACEHOLDER/i.test(code) || /^UNKNOWN/i.test(code);
    return (
        <Badge variant={isLegacy ? 'destructive' : 'outline'} className="font-mono text-xs">
            {code}
        </Badge>
    );
}

function CompanyCard({ item, isSurvivor, onSelect }: { item: GroupItem; isSurvivor: boolean; onSelect?: () => void }) {
    const company = item.company;
    return (
        <div
            onClick={onSelect}
            className={cn(
                'flex flex-col gap-2 rounded-lg border p-3 transition-colors',
                isSurvivor ? 'border-success bg-success-subtle' : 'border-border',
                onSelect && 'hover:border-primary/50 cursor-pointer',
            )}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                    <Building2 className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                    <span className="truncate font-medium">{company?.name ?? item.company_uuid}</span>
                </div>
                {isSurvivor && <Badge className="bg-success text-success-foreground shrink-0">Survives</Badge>}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 pl-6">
                <CodeBadge code={company?.company_code} />
                {company?.country && (
                    <Badge variant="outline" className="text-xs">
                        {company.country}
                    </Badge>
                )}
                <span className="text-muted-foreground text-xs">{item.linked_records_total ?? item.linked_records_count} linked records</span>
            </div>
            {item.linked_records && Object.keys(item.linked_records).length > 0 && (
                <div className="flex flex-wrap gap-1 pl-6">
                    {Object.entries(item.linked_records).map(([key, n]) => (
                        <span key={key} className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-xs">
                            {key}: {n}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CompanyCleanupEditor() {
    const { t } = useTranslations();
    const [stats, setStats] = useState<CleanupStats | null>(null);
    const [groups, setGroups] = useState<MergeGroup[]>([]);
    const [audits, setAudits] = useState<AuditEntry[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<MergeGroup | null>(null);
    const [survivorUuid, setSurvivorUuid] = useState<string>('');
    const [mergeConfirm, setMergeConfirm] = useState('');
    const [autoMergeConfirm, setAutoMergeConfirm] = useState('');
    const [autoMergeMinConfidence, setAutoMergeMinConfidence] = useState('80');
    const [autoMergeSkipRisky, setAutoMergeSkipRisky] = useState(true);
    const [selectedGroupUuids, setSelectedGroupUuids] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [previewImpact, setPreviewImpact] = useState<Record<string, number> | null>(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [registryCompanies, setRegistryCompanies] = useState<RegistryReviewCompany[]>([]);
    const [registrySel, setRegistrySel] = useState<Record<string, string>>({});
    const [scanJob, setScanJob] = useState<{ key: string; percent: number; message: string } | null>(null);

    const post = useCallback(async (url: string, body?: Record<string, unknown>, label?: string): Promise<Record<string, unknown> | null> => {
        setActionLoading(label ?? url);
        try {
            const res = await csrfFetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body ?? {}),
            });
            const data: Record<string, unknown> = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error((data.message as string) ?? 'Action failed');
                return null;
            }
            return data;
        } finally {
            setActionLoading(null);
        }
    }, []);

    const loadStats = useCallback(async () => {
        const res = await fetch(BASE, { headers: { Accept: 'application/json' } });
        if (res.ok) setStats(await res.json());
    }, []);

    const loadGroups = useCallback(async () => {
        const res = await fetch(`${BASE}/groups`, { headers: { Accept: 'application/json' } });
        if (res.ok) {
            const data = await res.json();
            setGroups(data.groups?.data ?? []);
        }
    }, []);

    const loadHistory = useCallback(async () => {
        const res = await fetch(`${BASE}/merge-history`, { headers: { Accept: 'application/json' } });
        if (res.ok) {
            const data = await res.json();
            setAudits(data.audits?.data ?? []);
        }
    }, []);

    const loadRegistry = useCallback(async () => {
        const res = await fetch(`${BASE}/registry/candidates`, { headers: { Accept: 'application/json' } });
        if (res.ok) {
            const data = await res.json();
            setRegistryCompanies((data.data ?? []) as RegistryReviewCompany[]);
        }
    }, []);

    const reload = useCallback(async () => {
        await Promise.all([loadStats(), loadGroups(), loadHistory(), loadRegistry()]);
    }, [loadStats, loadGroups, loadHistory, loadRegistry]);

    useEffect(() => {
        void reload();
    }, [reload]);

    // Poll a background ImportJob and surface its progress as a bar until it
    // completes, then run onDone (refresh the relevant data).
    const pollImportJob = useCallback((jobId: string, key: string, onDone: () => Promise<unknown> | void) => {
        const tick = async () => {
            const res = await fetch(`/settings/system/import-progress/${jobId}`, { headers: { Accept: 'application/json' } });
            if (!res.ok) {
                setScanJob(null);
                return;
            }
            const job = await res.json();
            if (job.status === 'completed' || job.status === 'failed') {
                setScanJob(null);
                if (job.status === 'completed') {
                    toast.success(String(job.message ?? 'Done'));
                } else {
                    toast.error(String(job.error ?? 'Failed'));
                }
                await onDone();
                return;
            }
            setScanJob({ key, percent: Number(job.progress ?? 0), message: String(job.message ?? 'Working…') });
            setTimeout(tick, 1200);
        };
        setScanJob({ key, percent: 0, message: 'Starting…' });
        setTimeout(tick, 800);
    }, []);

    // Default the per-company candidate selection to the top candidate.
    useEffect(() => {
        setRegistrySel((prev) => {
            const next = { ...prev };
            for (const company of registryCompanies) {
                if (!next[company.uuid] && company.candidates[0]) {
                    next[company.uuid] = company.candidates[0].uuid;
                }
            }
            return next;
        });
    }, [registryCompanies]);

    const openGroup = async (group: MergeGroup) => {
        const res = await fetch(`${BASE}/groups/${group.uuid}`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const data: MergeGroup = await res.json();
        setSelectedGroup(data);

        // Default survivor = item with most linked records.
        const items = data.items ?? [];
        const best = items.reduce<GroupItem | null>((prev, cur) => {
            if (!prev) return cur;
            const prevCount = prev.linked_records_total ?? prev.linked_records_count ?? 0;
            const curCount = cur.linked_records_total ?? cur.linked_records_count ?? 0;
            return curCount > prevCount ? cur : prev;
        }, null);
        setSurvivorUuid(best?.company_uuid ?? items[0]?.company_uuid ?? '');
        setMergeConfirm('');
        setPreviewImpact(null);
        setActiveTab('review');
    };

    const handleScanNormalize = async () => {
        setLoading(true);
        const data = await post(`${BASE}/scan/normalize`, {}, 'normalize');
        setLoading(false);
        if (data) {
            toast.success(String(data.message ?? 'Done'));
            await reload();
        }
    };

    const handleScanDuplicates = async () => {
        const data = await post(`${BASE}/scan/duplicates`, {}, 'duplicates');
        if (data?.import_job_id) {
            pollImportJob(String(data.import_job_id), 'duplicates', reload);
        } else if (data) {
            toast.success(String(data.message ?? 'Done'));
            await reload();
        }
    };

    const handleScanRegistry = async () => {
        const data = await post(`${BASE}/scan/registry`, {}, 'scan-registry');
        if (!data || !data.success) {
            return;
        }
        if (data.import_job_id) {
            pollImportJob(String(data.import_job_id), 'registry', () => Promise.all([loadStats(), loadRegistry()]));
        }
    };

    const handleApplyRegistry = async (candidateUuid: string) => {
        const data = await post(`${BASE}/registry/candidates/${candidateUuid}/apply`, {}, `apply-${candidateUuid}`);
        if (data) {
            if (data.outcome === 'applied') {
                toast.success(String(data.message ?? 'Applied.'));
            } else {
                // needs_merge or blocked — surface the reason.
                toast.warning(String(data.message ?? 'Queued for review.'));
            }
            await Promise.all([loadStats(), loadGroups(), loadRegistry()]);
        }
    };

    const handleRejectRegistry = async (candidateUuid: string) => {
        const data = await post(`${BASE}/registry/candidates/${candidateUuid}/reject`, {}, `reject-${candidateUuid}`);
        if (data) {
            toast.success(String(data.message ?? 'Rejected.'));
            await Promise.all([loadStats(), loadRegistry()]);
        }
    };

    // After approving/rejecting, jump straight to the next pending group so the
    // user can triage the queue without returning to the list each time.
    const goToNextGroup = (actedUuid: string) => {
        const pendingOrder = groups.filter((g) => g.status === 'pending').map((g) => g.uuid);
        const pos = pendingOrder.indexOf(actedUuid);
        const nextUuid = pendingOrder.slice(pos + 1).find((u) => u !== actedUuid) ?? pendingOrder.find((u) => u !== actedUuid) ?? null;
        const next = nextUuid ? (groups.find((g) => g.uuid === nextUuid) ?? null) : null;

        if (next) {
            void openGroup(next);
        } else {
            setSelectedGroup(null);
            setActiveTab('groups');
            toast.success('No more groups to review.');
        }
    };

    const handleApprove = async () => {
        if (!selectedGroup || !survivorUuid) return;
        const acted = selectedGroup.uuid;
        const data = await post(`${BASE}/groups/${acted}/approve`, { survivor_uuid: survivorUuid }, 'approve');
        if (data) {
            toast.success(t('toasts.settings_companycleanupeditor.group_approved', 'Group approved.'));
            goToNextGroup(acted);
            await reload();
        }
    };

    const handleReject = async () => {
        if (!selectedGroup) return;
        const acted = selectedGroup.uuid;
        const data = await post(`${BASE}/groups/${acted}/reject`, {}, 'reject');
        if (data) {
            toast.success(t('toasts.settings_companycleanupeditor.group_rejected', 'Group rejected.'));
            goToNextGroup(acted);
            await reload();
        }
    };

    const handlePreview = async () => {
        if (!selectedGroup) return;
        const data = await post(`${BASE}/groups/${selectedGroup.uuid}/preview`, {}, 'preview');
        if (data) setPreviewImpact((data.summary as Record<string, number>) ?? {});
    };

    const handleMerge = async () => {
        if (!selectedGroup || mergeConfirm !== 'MERGE' || !survivorUuid) return;
        const sourceUuids = (selectedGroup.items ?? []).map((i) => i.company_uuid).filter((id) => id !== survivorUuid);
        const data = await post(
            `${BASE}/groups/${selectedGroup.uuid}/merge`,
            { survivor_uuid: survivorUuid, source_company_uuids: sourceUuids, confirmation: 'MERGE' },
            'merge',
        );
        if (data) {
            toast.success(t('toasts.settings_companycleanupeditor.companies_merged', 'Companies merged successfully.'));
            setSelectedGroup(null);
            setMergeConfirm('');
            setPreviewImpact(null);
            await reload();
        }
    };

    const handleBulkAutoMerge = async () => {
        if (autoMergeConfirm !== 'AUTO-MERGE') return;
        setLoading(true);
        const data = await post(
            `${BASE}/groups/bulk-auto-merge`,
            { confirmation: 'AUTO-MERGE', min_confidence: Number(autoMergeMinConfidence) || 0, skip_risky: autoMergeSkipRisky },
            'bulk-auto-merge',
        );
        setLoading(false);
        if (data) {
            const failed = Number(data.failed ?? 0);
            const merged = Number(data.merged ?? 0);
            if (failed > 0) {
                toast.warning(
                    t(
                        'toasts.settings_companycleanupeditor.bulk_merge_partial',
                        'Merged :merged, but :failed group(s) failed — check console for details.',
                    )
                        .replace(':merged', String(merged))
                        .replace(':failed', String(failed)),
                );
                console.warn('Bulk auto-merge failures:', data.errors);
            } else {
                toast.success(String(data.message ?? 'Done'));
            }
            setAutoMergeConfirm('');
            await reload();
        }
    };

    const handleUnmerge = async (auditUuid: string) => {
        const data = await post(`${BASE}/merge-history/${auditUuid}/unmerge`, {}, `unmerge-${auditUuid}`);
        if (data) {
            toast.success(t('toasts.settings_companycleanupeditor.merge_reversed', 'Merge reversed.'));
            await reload();
        }
    };

    // ── Batch triage ──
    const toggleGroupSelect = (uuid: string) => {
        setSelectedGroupUuids((prev) => {
            const next = new Set(prev);
            if (next.has(uuid)) {
                next.delete(uuid);
            } else {
                next.add(uuid);
            }
            return next;
        });
    };

    const handleBatchApprove = async () => {
        const targets = groups.filter((g) => g.status === 'pending' && selectedGroupUuids.has(g.uuid));
        if (targets.length === 0) return;
        setLoading(true);
        let approved = 0;
        for (const group of targets) {
            const survivor = group.suggested_surviving_company_uuid ?? (group.enrichedItems ?? group.items ?? [])[0]?.company_uuid;
            if (!survivor) continue;
            const data = await post(`${BASE}/groups/${group.uuid}/approve`, { survivor_uuid: survivor }, `batch-approve-${group.uuid}`);
            if (data) approved++;
        }
        setLoading(false);
        toast.success(t('toasts.settings_companycleanupeditor.batch_approved', 'Approved :count group(s).').replace(':count', String(approved)));
        setSelectedGroupUuids(new Set());
        await reload();
    };

    const handleBatchReject = async () => {
        const targets = groups.filter((g) => g.status === 'pending' && selectedGroupUuids.has(g.uuid));
        if (targets.length === 0) return;
        setLoading(true);
        let rejected = 0;
        for (const group of targets) {
            const data = await post(`${BASE}/groups/${group.uuid}/reject`, {}, `batch-reject-${group.uuid}`);
            if (data) rejected++;
        }
        setLoading(false);
        toast.success(t('toasts.settings_companycleanupeditor.batch_rejected', 'Rejected :count group(s).').replace(':count', String(rejected)));
        setSelectedGroupUuids(new Set());
        await reload();
    };

    // Keyboard triage in Merge Review: A = approve, R = reject (both auto-advance).
    useEffect(() => {
        if (activeTab !== 'review' || !selectedGroup) return;
        const onKey = (e: KeyboardEvent) => {
            const el = e.target as HTMLElement | null;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const key = e.key.toLowerCase();
            if (key === 'a' && selectedGroup.status === 'pending' && survivorUuid) {
                e.preventDefault();
                void handleApprove();
            } else if (key === 'r' && (selectedGroup.status === 'pending' || selectedGroup.status === 'approved')) {
                e.preventDefault();
                void handleReject();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, selectedGroup, survivorUuid]);

    const pendingGroups = groups.filter((g) => g.status === 'pending');
    const approvedGroups = groups.filter((g) => g.status === 'approved');
    const mergedGroups = groups.filter((g) => g.status === 'merged');
    const selectedPendingCount = pendingGroups.filter((g) => selectedGroupUuids.has(g.uuid)).length;

    const selectedItems = selectedGroup?.items ?? [];
    const sourceItems = selectedItems.filter((i) => i.company_uuid !== survivorUuid);
    const survivorItem = selectedItems.find((i) => i.company_uuid === survivorUuid);

    return (
        <div className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="h-auto flex-wrap gap-1">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="groups">
                        Duplicate Groups
                        {pendingGroups.length > 0 && (
                            <span className="bg-warning text-warning-foreground ml-1.5 rounded-full px-1.5 py-0.5 text-xs">
                                {pendingGroups.length}
                            </span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="review">
                        Merge Review
                        {selectedGroup && <span className="bg-info ml-1.5 h-2 w-2 rounded-full" />}
                    </TabsTrigger>
                    <TabsTrigger value="registry">
                        Registry matches
                        {(stats?.registry_pending_review ?? 0) > 0 && (
                            <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-xs text-white">
                                {stats?.registry_pending_review}
                            </span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="history">History</TabsTrigger>
                </TabsList>

                {/* ── Overview ── */}
                <TabsContent value="overview" className="space-y-4">
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {[
                            { label: 'Active companies', value: stats?.active_companies ?? '—' },
                            { label: 'Already merged', value: stats?.already_merged_companies ?? '—' },
                            { label: 'Pending groups', value: pendingGroups.length, highlight: pendingGroups.length > 0 },
                            { label: 'Approved groups', value: approvedGroups.length },
                            { label: 'Total merged groups', value: mergedGroups.length },
                            { label: 'Soft deleted', value: stats?.soft_deleted_companies ?? '—' },
                        ].map(({ label, value, highlight }) => (
                            <Card key={label} className={cn('py-3', highlight && 'border-warning')}>
                                <CardContent className="px-4 py-0">
                                    <div className="text-muted-foreground text-xs">{label}</div>
                                    <div className={cn('text-2xl font-bold tabular-nums', highlight && 'text-warning-subtle-foreground')}>
                                        {value}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Step-by-step actions */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Step-by-step cleanup</CardTitle>
                            <CardDescription>Run in order. Each step builds on the previous one.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                <div className="bg-muted flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold">1</div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium">Normalize company names &amp; codes</div>
                                    <div className="text-muted-foreground text-xs">
                                        Strips legal forms, diacritics, and punctuation so names can be compared fairly.
                                    </div>
                                </div>
                                <Button size="sm" variant="outline" onClick={handleScanNormalize} disabled={loading || actionLoading !== null}>
                                    {actionLoading === 'normalize' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                                    Normalize
                                </Button>
                            </div>
                            <Separator />
                            <div className="space-y-2">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <div className="bg-muted flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold">2</div>
                                    <div className="flex-1">
                                        <div className="text-sm font-medium">Scan for duplicates</div>
                                        <div className="text-muted-foreground text-xs">
                                            Finds matching pairs including companies with LEGACY or placeholder codes. Creates merge groups.
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleScanDuplicates}
                                        disabled={loading || actionLoading !== null || scanJob !== null}
                                    >
                                        {scanJob?.key === 'duplicates' || actionLoading === 'duplicates' ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Wand2 className="h-4 w-4" />
                                        )}
                                        Scan duplicates
                                    </Button>
                                </div>
                                {scanJob?.key === 'duplicates' && (
                                    <div className="space-y-1">
                                        <Progress value={scanJob.percent} />
                                        <div className="text-muted-foreground text-xs tabular-nums">
                                            {scanJob.message} ({scanJob.percent}%)
                                        </div>
                                    </div>
                                )}
                            </div>
                            <Separator />
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                <div className="bg-muted flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold">3</div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium">Review &amp; merge</div>
                                    <div className="text-muted-foreground text-xs">
                                        Open Duplicate Groups, click Review on any group, pick the survivor, then merge — or use Auto-merge below.
                                    </div>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => setActiveTab('groups')} disabled={pendingGroups.length === 0}>
                                    <ChevronRight className="h-4 w-4" />
                                    View groups
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Auto-merge */}
                    {(pendingGroups.length > 0 || approvedGroups.length > 0) && (
                        <Card className="border-warning">
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Zap className="text-warning h-4 w-4" />
                                    Auto-merge all groups
                                </CardTitle>
                                <CardDescription>
                                    Merges pending/approved groups automatically. The registry-matched company (or, otherwise, the one with the most
                                    linked records) survives each merge. Full audit trail is saved and every merge can be undone from History.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex flex-wrap items-center gap-4">
                                    <label className="flex items-center gap-2 text-sm">
                                        <span className="text-muted-foreground">Only merge confidence ≥</span>
                                        <Input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={autoMergeMinConfidence}
                                            onChange={(e) => setAutoMergeMinConfidence(e.target.value)}
                                            className="w-20 tabular-nums"
                                        />
                                        <span className="text-muted-foreground">%</span>
                                    </label>
                                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={autoMergeSkipRisky}
                                            onChange={(e) => setAutoMergeSkipRisky(e.target.checked)}
                                            className="accent-primary h-4 w-4"
                                        />
                                        <span>Skip risky groups (conflicting code / VAT / country)</span>
                                    </label>
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Type AUTO-MERGE to confirm"
                                        value={autoMergeConfirm}
                                        onChange={(e) => setAutoMergeConfirm(e.target.value)}
                                        className="max-w-xs font-mono"
                                    />
                                    <Button
                                        variant="destructive"
                                        disabled={autoMergeConfirm !== 'AUTO-MERGE' || loading || actionLoading !== null}
                                        onClick={handleBulkAutoMerge}
                                    >
                                        {(loading || actionLoading === 'bulk-auto-merge') && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Auto-merge
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* ── Duplicate Groups ── */}
                <TabsContent value="groups" className="space-y-2">
                    {groups.length === 0 ? (
                        <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
                            No groups yet. Run <strong>Normalize</strong> then <strong>Scan duplicates</strong> from the Overview tab.
                        </div>
                    ) : (
                        <>
                            {/* Batch action bar */}
                            {selectedPendingCount > 0 && (
                                <div className="bg-accent flex flex-wrap items-center gap-2 rounded-lg border p-2">
                                    <span className="text-sm font-medium">{selectedPendingCount} selected</span>
                                    <Button size="sm" variant="outline" onClick={handleBatchApprove} disabled={loading || actionLoading !== null}>
                                        <CheckCircle2 className="mr-1 h-3 w-3" />
                                        Approve selected
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-danger-subtle-foreground"
                                        onClick={handleBatchReject}
                                        disabled={loading || actionLoading !== null}
                                    >
                                        <Trash2 className="mr-1 h-3 w-3" />
                                        Reject selected
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setSelectedGroupUuids(new Set())}>
                                        Clear
                                    </Button>
                                </div>
                            )}
                            {(['pending', 'approved', 'rejected', 'merged'] as const).map((status) => {
                                const filtered = groups.filter((g) => g.status === status);
                                if (filtered.length === 0) return null;
                                return (
                                    <div key={status} className="space-y-1">
                                        <div className="text-muted-foreground flex items-center gap-2 px-1 text-xs font-semibold tracking-wide uppercase">
                                            <span>
                                                {status} ({filtered.length})
                                            </span>
                                            {status === 'pending' && (
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    size="sm"
                                                    className="h-auto p-0 text-xs normal-case"
                                                    onClick={() =>
                                                        setSelectedGroupUuids((prev) => {
                                                            const allSelected = filtered.every((g) => prev.has(g.uuid));
                                                            const next = new Set(prev);
                                                            filtered.forEach((g) => (allSelected ? next.delete(g.uuid) : next.add(g.uuid)));
                                                            return next;
                                                        })
                                                    }
                                                >
                                                    {filtered.every((g) => selectedGroupUuids.has(g.uuid)) ? 'Deselect all' : 'Select all'}
                                                </Button>
                                            )}
                                        </div>
                                        {filtered.map((group) => {
                                            const items = group.enrichedItems ?? group.items ?? [];
                                            return (
                                                <div
                                                    key={group.uuid}
                                                    className={cn(
                                                        'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                                                        selectedGroup?.uuid === group.uuid && 'border-primary/50 bg-primary/5',
                                                        group.risk && 'border-warning',
                                                    )}
                                                >
                                                    {status === 'pending' && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedGroupUuids.has(group.uuid)}
                                                            onChange={() => toggleGroupSelect(group.uuid)}
                                                            className="accent-primary h-4 w-4 shrink-0"
                                                            aria-label="Select group"
                                                        />
                                                    )}
                                                    <ConfidenceBadge score={group.confidence_score} />
                                                    <div className="min-w-0 flex-1 space-y-0.5">
                                                        {items.map((item) => (
                                                            <div key={item.company_uuid} className="flex items-center gap-1.5 text-sm">
                                                                <Building2 className="text-muted-foreground h-3 w-3 shrink-0" />
                                                                <span className="truncate font-medium">
                                                                    {item.company?.name ?? item.company_uuid}
                                                                </span>
                                                                <CodeBadge code={item.company?.company_code} />
                                                                <span className="text-muted-foreground text-xs">({item.linked_records_count})</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {group.risk && (
                                                        <span className="bg-warning-subtle text-warning-subtle-foreground inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium">
                                                            <AlertTriangle className="h-3 w-3" />
                                                            {RISK_LABELS[group.risk] ?? 'Review needed'}
                                                        </span>
                                                    )}
                                                    <span className={cn('shrink-0 rounded px-2 py-0.5 text-xs font-medium', statusColor[status])}>
                                                        {status}
                                                    </span>
                                                    {status !== 'merged' && status !== 'rejected' && (
                                                        <Button size="sm" variant="outline" onClick={() => openGroup(group)}>
                                                            Review
                                                            <ChevronRight className="ml-1 h-3 w-3" />
                                                        </Button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </TabsContent>

                {/* ── Merge Review ── */}
                <TabsContent value="review" className="space-y-4">
                    {!selectedGroup ? (
                        <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
                            Select a group from the <strong>Duplicate Groups</strong> tab to review it here.
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className={cn('rounded px-2 py-0.5 text-xs font-medium', statusColor[selectedGroup.status])}>
                                        {selectedGroup.status}
                                    </span>
                                    <ConfidenceBadge score={selectedGroup.confidence_score} />
                                    <span className="text-muted-foreground font-mono text-xs">{selectedGroup.uuid.slice(0, 8)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground hidden text-xs sm:inline">
                                        Shortcuts: <kbd className="bg-muted rounded px-1">A</kbd> approve ·{' '}
                                        <kbd className="bg-muted rounded px-1">R</kbd> reject
                                    </span>
                                    <Button size="sm" variant="ghost" onClick={() => setSelectedGroup(null)} className="text-muted-foreground">
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            {/* Risk warning */}
                            {selectedGroup.risk && (
                                <div className="bg-warning-subtle text-warning-subtle-foreground border-warning flex items-center gap-2 rounded-lg border p-2 text-sm">
                                    <AlertTriangle className="h-4 w-4 shrink-0" />
                                    <span>
                                        These companies disagree on {RISK_LABELS[selectedGroup.risk]?.toLowerCase() ?? 'key identity fields'} — they
                                        may be different entities. Bulk auto-merge skips this group; review carefully before merging.
                                    </span>
                                </div>
                            )}

                            {/* Company cards — click to switch survivor */}
                            <div className="space-y-1">
                                <div className="text-muted-foreground mb-2 text-xs">
                                    Click a company to set it as the survivor. All others will be merged into it and deleted.
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {selectedItems.map((item) => (
                                        <CompanyCard
                                            key={item.company_uuid}
                                            item={item}
                                            isSurvivor={item.company_uuid === survivorUuid}
                                            onSelect={() => setSurvivorUuid(item.company_uuid)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Merge direction indicator */}
                            {sourceItems.length > 0 && survivorItem && (
                                <div className="text-muted-foreground flex flex-wrap items-center gap-1 text-sm">
                                    {sourceItems.map((s) => (
                                        <span key={s.company_uuid} className="text-destructive font-medium">
                                            {s.company?.name ?? s.company_uuid}
                                        </span>
                                    ))}
                                    <ArrowRight className="h-4 w-4" />
                                    <span className="text-success-subtle-foreground font-medium">
                                        {survivorItem.company?.name ?? survivorItem.company_uuid}
                                    </span>
                                </div>
                            )}

                            {/* Impact preview */}
                            {previewImpact && (
                                <Card className="border-info bg-info-subtle">
                                    <CardContent className="py-3">
                                        <div className="mb-1 text-sm font-medium">Records that will be moved</div>
                                        <div className="flex flex-wrap gap-2">
                                            {Object.entries(previewImpact)
                                                .filter(([, n]) => n > 0)
                                                .map(([key, n]) => (
                                                    <span
                                                        key={key}
                                                        className="bg-info-subtle text-info-subtle-foreground rounded px-2 py-0.5 text-xs"
                                                    >
                                                        {key}: {n}
                                                    </span>
                                                ))}
                                            {Object.values(previewImpact).every((n) => n === 0) && (
                                                <span className="text-muted-foreground text-xs">No records to move.</span>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Actions */}
                            <div className="flex flex-wrap gap-2">
                                {selectedGroup.status === 'pending' && (
                                    <Button size="sm" variant="outline" onClick={handleApprove} disabled={!survivorUuid || actionLoading !== null}>
                                        {actionLoading === 'approve' ? (
                                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                        ) : (
                                            <CheckCircle2 className="mr-1 h-3 w-3" />
                                        )}
                                        Approve
                                    </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={handlePreview} disabled={actionLoading !== null}>
                                    {actionLoading === 'preview' ? (
                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    ) : (
                                        <Clock className="mr-1 h-3 w-3" />
                                    )}
                                    Preview impact
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-destructive hover:text-destructive"
                                    onClick={handleReject}
                                    disabled={actionLoading !== null}
                                >
                                    {actionLoading === 'reject' ? (
                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    ) : (
                                        <Trash2 className="mr-1 h-3 w-3" />
                                    )}
                                    Reject
                                </Button>
                            </div>

                            {selectedGroup.status === 'approved' && (
                                <Card className="border-warning bg-warning-subtle">
                                    <CardContent className="space-y-3 py-3">
                                        <div className="text-warning-subtle-foreground flex items-center gap-2 text-sm font-medium">
                                            <AlertTriangle className="h-4 w-4 shrink-0" />
                                            This action is irreversible. The source companies will be soft-deleted.
                                        </div>
                                        <div className="flex gap-2">
                                            <Input
                                                value={mergeConfirm}
                                                onChange={(e) => setMergeConfirm(e.target.value)}
                                                placeholder="Type MERGE to confirm"
                                                className="max-w-[200px] font-mono"
                                            />
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                disabled={mergeConfirm !== 'MERGE' || !survivorUuid || actionLoading !== null}
                                                onClick={handleMerge}
                                            >
                                                {actionLoading === 'merge' && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                                Merge
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}
                </TabsContent>

                {/* ── Registry matches ── */}
                <TabsContent value="registry" className="space-y-4">
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {[
                            { label: 'Mirror entries', value: stats?.registry_mirror_entries ?? '—' },
                            {
                                label: 'Pending review',
                                value: stats?.registry_pending_review ?? 0,
                                highlight: (stats?.registry_pending_review ?? 0) > 0,
                            },
                            { label: 'Matched', value: stats?.registry_matched ?? 0 },
                            { label: 'Candidates', value: stats?.registry_candidates ?? 0 },
                        ].map(({ label, value, highlight }) => (
                            <Card key={label} className={cn('py-3', highlight && 'border-amber-400')}>
                                <CardContent className="px-4 py-0">
                                    <div className="text-muted-foreground text-xs">{label}</div>
                                    <div className={cn('text-2xl font-bold tabular-nums', highlight && 'text-amber-600')}>{value}</div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {(stats?.registry_mirror_entries ?? 0) === 0 ? (
                        <Card className="border-amber-400 bg-amber-50 dark:bg-amber-950/20">
                            <CardContent className="flex items-start gap-2 py-3 text-sm">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                                <div>
                                    The Registrų Centras mirror is empty. Import it first by running{' '}
                                    <code className="bg-muted rounded px-1 py-0.5 font-mono text-xs">php artisan import:lt-gov-data</code> on the
                                    server, then come back here to scan.
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Database className="h-4 w-4" />
                                    Scan &amp; auto-apply
                                </CardTitle>
                                <CardDescription>
                                    Matches active Lithuanian companies against the registry. Near-certain matches (code-confirmed with a strong name,
                                    or a clearly dominant name) are applied automatically — name, code, VAT and address are filled in and the old
                                    values are kept as an alias. Everything ambiguous is queued below for review.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleScanRegistry}
                                    disabled={scanJob !== null || actionLoading !== null}
                                >
                                    {scanJob?.key === 'registry' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                                    {scanJob?.key === 'registry' ? 'Scanning…' : 'Scan & auto-apply'}
                                </Button>
                                {scanJob?.key === 'registry' && (
                                    <div className="space-y-1">
                                        <Progress value={scanJob.percent} />
                                        <div className="text-muted-foreground text-xs tabular-nums">
                                            {scanJob.message} ({scanJob.percent}%)
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Review queue */}
                    {registryCompanies.length === 0 ? (
                        <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
                            Nothing waiting for review. Run a scan above — matches that aren&apos;t certain enough to auto-apply will appear here.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {registryCompanies.map((company) => {
                                const selectedUuid = registrySel[company.uuid] ?? company.candidates[0]?.uuid;
                                const candidate = company.candidates.find((c) => c.uuid === selectedUuid) ?? company.candidates[0];
                                return (
                                    <div key={company.uuid} className="rounded-lg border p-3">
                                        {company.status === 'needs_merge' && (
                                            <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                                                <AlertTriangle className="h-3.5 w-3.5" />
                                                Another company already holds this identity — resolve via Merge Review.
                                            </div>
                                        )}
                                        <div className="grid gap-3 sm:grid-cols-2">
                                            {/* Current */}
                                            <div className="space-y-1">
                                                <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">Current</div>
                                                <div className="flex items-center gap-1.5 text-sm font-medium">
                                                    <Building2 className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                                                    <span className="truncate">{company.current.name}</span>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <CodeBadge code={company.current.company_code} />
                                                    {company.current.vat && (
                                                        <span className="text-muted-foreground text-xs">VAT {company.current.vat}</span>
                                                    )}
                                                </div>
                                                <div className="text-muted-foreground flex items-start gap-1 text-xs">
                                                    <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
                                                    <span>{company.current.address ?? 'no address'}</span>
                                                </div>
                                            </div>

                                            {/* Proposed */}
                                            <div className="space-y-1 rounded-md bg-emerald-50 p-2 dark:bg-emerald-950/20">
                                                <div className="flex items-center justify-between">
                                                    <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                                                        Proposed (registry)
                                                    </div>
                                                    {candidate && <ConfidenceBadge score={candidate.name_score} />}
                                                </div>
                                                {candidate ? (
                                                    <>
                                                        <div className="flex items-center gap-1.5 text-sm font-medium">
                                                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                                            <span className="truncate">{candidate.registry_name}</span>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-1.5">
                                                            <CodeBadge code={candidate.registry_code} />
                                                            {candidate.registry_vat && (
                                                                <span className="text-muted-foreground text-xs">VAT {candidate.registry_vat}</span>
                                                            )}
                                                            {candidate.code_match ? (
                                                                <Badge variant="outline" className="text-xs">
                                                                    code match
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="outline" className="text-xs">
                                                                    by name
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="text-muted-foreground flex items-start gap-1 text-xs">
                                                            <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
                                                            <span>{candidate.address ?? 'no address'}</span>
                                                        </div>
                                                        {candidate.name_disagrees && (
                                                            <div className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                                                                <AlertTriangle className="h-3 w-3" />
                                                                Code matched but the name is quite different — verify before applying.
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="text-muted-foreground text-xs">No candidate.</div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Candidate picker (when more than one) */}
                                        {company.candidates.length > 1 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                                {company.candidates.map((c) => (
                                                    <Button
                                                        key={c.uuid}
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setRegistrySel((prev) => ({ ...prev, [company.uuid]: c.uuid }))}
                                                        className={cn(
                                                            'h-auto rounded px-2 py-0.5 text-xs font-normal',
                                                            c.uuid === selectedUuid && 'border-primary bg-primary/10',
                                                        )}
                                                    >
                                                        {c.registry_name} · {Math.round(c.name_score)}%
                                                    </Button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                disabled={!candidate || actionLoading !== null}
                                                onClick={() => candidate && handleApplyRegistry(candidate.uuid)}
                                            >
                                                {actionLoading === `apply-${candidate?.uuid}` ? (
                                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                ) : (
                                                    <CheckCircle2 className="mr-1 h-3 w-3" />
                                                )}
                                                Apply
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="text-red-600 hover:text-red-700"
                                                disabled={!candidate || actionLoading !== null}
                                                onClick={() => candidate && handleRejectRegistry(candidate.uuid)}
                                            >
                                                {actionLoading === `reject-${candidate?.uuid}` ? (
                                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                ) : (
                                                    <Trash2 className="mr-1 h-3 w-3" />
                                                )}
                                                Reject
                                            </Button>
                                            {company.status === 'needs_merge' && (
                                                <Button size="sm" variant="ghost" onClick={() => setActiveTab('groups')}>
                                                    <ChevronRight className="mr-1 h-3 w-3" />
                                                    Go to Merge Review
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                {/* ── History ── */}
                <TabsContent value="history">
                    {audits.length === 0 ? (
                        <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">No merge history yet.</div>
                    ) : (
                        <ScrollArea className="h-[500px]">
                            <div className="space-y-2 pr-2">
                                {audits.map((audit) => (
                                    <div key={audit.uuid} className="flex flex-col gap-1 rounded-lg border p-3 text-sm">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-muted-foreground text-xs tabular-nums">
                                                {new Date(audit.performed_at).toLocaleString()}
                                            </span>
                                            {audit.reversed_at ? (
                                                <span className="text-muted-foreground text-xs italic">Reversed</span>
                                            ) : audit.reversible ? (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleUnmerge(audit.uuid)}
                                                    disabled={actionLoading !== null}
                                                >
                                                    {actionLoading === `unmerge-${audit.uuid}` ? (
                                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                    ) : (
                                                        <RefreshCw className="mr-1 h-3 w-3" />
                                                    )}
                                                    Undo
                                                </Button>
                                            ) : null}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1">
                                            <span className="text-destructive font-medium">
                                                {audit.source_company_name ?? audit.source_company_uuid.slice(0, 8)}
                                            </span>
                                            <ArrowRight className="text-muted-foreground h-3.5 w-3.5" />
                                            <span className="text-success-subtle-foreground font-medium">
                                                {audit.target_company_name ?? audit.target_company_uuid.slice(0, 8)}
                                            </span>
                                        </div>
                                        {audit.moved_records_summary && Object.values(audit.moved_records_summary).some((n) => n > 0) && (
                                            <div className="flex flex-wrap gap-1">
                                                {Object.entries(audit.moved_records_summary)
                                                    .filter(([, n]) => n > 0)
                                                    .map(([key, n]) => (
                                                        <span key={key} className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs">
                                                            {key}: {n}
                                                        </span>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
